import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useShellUI } from '../../shell/AppShell';
import { useReveal, useCountUp } from '../../shell/motion';
import CurveChart from './CurveChart';
import CurveHistorical from './CurveHistorical';
import CurveHistory from './CurveHistory';
import CurveRail from './CurveRail';
import SaveStreak from './SaveStreak';
import AddToHomeScreen from './AddToHomeScreen';
import { fetchToday, lockDraw, fetchTape, getGuestToken, clearGuestToken, claimGuestDraws } from './curveApi';
import { useCurveCeremony } from './curveFormat';
import AdPlaceholder from '../../utils/AdPlaceholder';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/* ════════ The Curve — today's draw ════════
   One player a day. Real trailing value, a blank future, your thumb.
   Lock it to see what everyone else drew. Reality marks it every morning. */

// 2026 NFL season anchors — used to phrase settle dates as season weeks.
// (Graduates to server config when the season rolls.)
const SEASON = { week1Tuesday: '2026-09-08', weeks: 18 };

/** "Week 6" when the date lands in the season, otherwise "Aug 28". */
function resolveLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00Z`);
  const w1 = new Date(`${SEASON.week1Tuesday}T12:00:00Z`);
  const week = Math.floor((d - w1) / (7 * 86400000)) + 1;
  if (week >= 1 && week <= SEASON.weeks) return `Week ${week}`;
  if (week > SEASON.weeks) return 'after the draft';
  if (week === 0) return 'kickoff';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function addDaysStr(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtTick(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase();
}

function fmtDate(dateStr) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
}

const styles = `
  .cv-page { min-height: calc(100vh - 60px); background: #FFFFFF; color: #1D1D1F;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    --brand:#FF7A00; --brand-soft:rgba(255,122,0,.13); --brand-ink:#C25E00;
    --ink:#1D1D1F; --ink2:#6E6E73; --ink3:#86868B; --surface:#F5F5F7; --line:#EBEBED;
    --green:#34C759; --green-ink:#1B873F; --green-soft:rgba(52,199,89,.14);
    --paper:#FFFDF8; --paper-edge:#EFE4D2;
    --mono:'SF Mono', ui-monospace, 'Menlo', monospace; --ease:cubic-bezier(0.16,1,0.3,1); }
  .cv-page * { box-sizing: border-box; }
  .cv-page button { font: inherit; color: inherit; background: none; border: none; cursor: pointer; }

  .cv-wrap { max-width: 720px; margin: 0 auto; padding: 34px 18px 90px; }
  .cv-kick { display: flex; align-items: baseline; gap: 10px; font-family: var(--mono); font-size: 10.5px;
    font-weight: 700; letter-spacing: .2em; color: var(--brand-ink); margin-bottom: 14px; }
  .cv-kick .n { margin-left: auto; color: var(--ink3); letter-spacing: .08em; }

  /* player header */
  .cv-player { display: flex; align-items: center; gap: 14px; margin-bottom: 6px; }
  .cv-ava { width: 56px; height: 56px; border-radius: 50%; background: #D2D2D7; overflow: hidden; flex: none;
    display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 16px; position: relative; }
  .cv-ava img { width: 100%; height: 100%; object-fit: cover; position: absolute; inset: 0; }
  .cv-name { font-size: 30px; font-weight: 700; letter-spacing: -.03em; line-height: 1.02; }
  .cv-meta { font-family: var(--mono); font-size: 11px; color: var(--ink3); margin-top: 4px; letter-spacing: .04em; }
  .cv-meta a { color: var(--brand-ink); text-decoration: none; font-weight: 700; }
  .cv-meta a:hover { text-decoration: underline; }
  .cv-ask { font-size: 17px; color: var(--ink2); line-height: 1.5; margin: 12px 0 16px; }
  .cv-ask b { color: var(--ink); font-weight: 700; }

  /* chart card */
  .cv-card { background: #fff; border: 1px solid var(--line); border-radius: 18px; padding: 10px 8px 6px;
    box-shadow: 0 14px 40px rgba(0,0,0,.07); margin-bottom: 14px; }
  .cv-legend { display: flex; gap: 14px; flex-wrap: wrap; padding: 4px 12px 10px; }
  .cv-lg { display: inline-flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 9px;
    font-weight: 600; letter-spacing: .06em; color: var(--ink3); }
  .cv-lg i { width: 18px; border-top: 3px solid; border-radius: 2px; }
  .cv-lg.h i { border-color: #1D1D1F; } .cv-lg.y i { border-color: var(--brand); }
  .cv-lg.b i { border-color: #EFE4D2; border-top-width: 8px; } .cv-lg.r i { border-color: #4E7FD0; }

  /* actions */
  .cv-actions { display: flex; flex-direction: column; gap: 10px; margin-bottom: 8px; }
  .cv-page .cv-lock { width: 100%; background: #000; color: #fff; padding: 17px; border-radius: 980px; font-size: 17px;
    font-weight: 600; transition: all .25s var(--ease); min-height: 52px; }
  .cv-page .cv-lock:hover:not(:disabled) { transform: translateY(-1px); opacity: .9; }
  .cv-page .cv-lock:disabled { background: var(--surface); color: var(--ink3); cursor: default; }
  .cv-page .cv-redraw { align-self: center; color: var(--ink2); font-size: 14px; font-weight: 500; padding: 10px 18px;
    border-radius: 980px; min-height: 44px; }
  .cv-page .cv-redraw:hover { background: var(--surface); }
  .cv-note { text-align: center; font-size: 12.5px; color: var(--ink3); }

  /* locked state */
  .cv-read { background: var(--paper); border: 1px solid var(--paper-edge); border-radius: 14px;
    padding: 14px 16px; font-size: 14.5px; color: var(--ink2); line-height: 1.55; margin-bottom: 12px; }
  .cv-read b { color: var(--ink); font-weight: 600; }
  .cv-read .hot { color: var(--brand-ink); font-weight: 600; }
  /* you vs the room meter */
  .cv-meter { background: var(--paper); border: 1px solid var(--paper-edge); border-radius: 14px;
    padding: 16px 18px 14px; margin-bottom: 12px; }
  .cv-meter-k { display: flex; justify-content: space-between; font-family: var(--mono); font-size: 9px;
    font-weight: 700; letter-spacing: .14em; color: var(--ink3); margin-bottom: 10px; }
  /* The field — every call is a dot, you're the orange one. No track, no knob:
     the old dot-on-a-pill meter read as a draggable slider. */
  .cv-field { position: relative; height: 66px; }
  .cv-field .fbase { position: absolute; left: 0; right: 0; bottom: 18px; height: 1px; background: rgba(0,0,0,.10); }
  .cv-field .fband { position: absolute; bottom: 17px; height: 3px; border-radius: 2px; background: #EFE4D2; }
  .cv-field .fcall { position: absolute; width: 9px; height: 9px; border-radius: 50%; background: #C0B49B;
    transform: translateX(-50%); }
  .cv-field .fyou { position: absolute; bottom: 13px; width: 17px; height: 17px; border-radius: 50%;
    background: var(--brand); border: 3px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.22);
    transform: translateX(-50%); }
  .cv-field .fyoulab { position: absolute; top: 0; transform: translateX(-50%); font-family: var(--mono);
    font-size: 10px; font-weight: 700; color: var(--brand-ink); white-space: nowrap; letter-spacing: .04em; }
  .cv-field .fmed { position: absolute; bottom: 10px; width: 2px; height: 36px; background: #D2D2D7;
    transform: translateX(-50%); }
  .cv-field .fmedlab { position: absolute; bottom: -6px; transform: translateX(-50%); white-space: nowrap;
    font-family: var(--mono); font-size: 10px; color: var(--ink2); letter-spacing: .04em; }
  .cv-field .fmedlab b { color: var(--ink); }
  .cv-meter-cap { display: flex; justify-content: space-between; font-family: var(--mono); font-size: 10px;
    color: var(--ink2); margin-top: 8px; letter-spacing: .04em; }
  .cv-meter-cap b { color: var(--ink); }
  .cv-meter-verdict { text-align: center; font-size: 13.5px; font-weight: 600; color: var(--brand-ink); margin-top: 11px; }
  .cv-stats { display: flex; gap: 8px; margin-bottom: 14px; }
  .cv-stat { flex: 1; background: var(--surface); border-radius: 12px; padding: 12px 14px; }
  .cv-stat .n { font-size: 20px; font-weight: 700; letter-spacing: -.02em; }
  .cv-stat .k { font-family: var(--mono); font-size: 8.5px; font-weight: 700; letter-spacing: .14em;
    color: var(--ink3); margin-top: 3px; }

  /* save the streak (guest) */
  .cv-save { background: var(--paper); border: 1px solid var(--paper-edge); border-radius: 16px;
    padding: 20px; margin-top: 12px; }
  .cv-save-t { font-size: 18px; font-weight: 700; letter-spacing: -.01em; margin-bottom: 4px; }
  .cv-save-s { font-size: 13px; color: var(--ink2); margin-bottom: 12px; line-height: 1.45; }
  .cv-save-form { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .cv-in { min-width: 0; background: #fff; border: 1px solid var(--line); border-radius: 12px;
    font-size: 16px; font-family: inherit; padding: 13px 14px; color: var(--ink); outline: none; }
  .cv-in:focus { box-shadow: 0 0 0 3px rgba(0,0,0,.06); }
  .cv-page .cv-save-btn { grid-column: 1 / -1; background: #000; color: #fff; border-radius: 980px; padding: 14px;
    font-size: 15px; font-weight: 600; min-height: 48px; }
  .cv-page .cv-save-btn:disabled { opacity: .4; cursor: default; }
  .cv-save-sent { font-size: 14px; color: var(--green-ink); font-weight: 500; }
  .cv-save-err { font-size: 12.5px; color: #FF3B30; margin-top: 8px; }

  /* closed / loading */
  .cv-closed { text-align: center; padding: 90px 20px; }
  .cv-closed h1 { font-size: 34px; font-weight: 700; letter-spacing: -.03em; margin-bottom: 10px; }
  .cv-closed p { font-size: 15px; color: var(--ink2); }

  /* next-morning ceremony — "since you last looked" */
  .cv-ceremony { background: var(--paper); border: 1px solid var(--paper-edge); border-radius: 14px;
    padding: 14px 16px; margin-bottom: 18px; }
  .cv-ceremony .k { display: block; font-family: var(--mono); font-size: 9.5px; font-weight: 700;
    letter-spacing: .16em; text-transform: uppercase; color: var(--brand-ink); margin-bottom: 5px; }
  .cv-ceremony p { font-size: 14px; color: var(--ink2); line-height: 1.55; margin: 0; }

  /* lock button settles, streak flame pops */
  @keyframes cvLockSettle { 0% { transform: scale(1); } 45% { transform: scale(.96); } 100% { transform: scale(1); } }
  .cv-page .cv-lock.is-locked { background: #000; color: #fff; cursor: default; animation: cvLockSettle .28s var(--ease); }
  .cv-flame { display: inline-block; }
  @keyframes cvFlamePop {
    0% { transform: scale(1) rotate(0deg); }
    45% { transform: scale(1.28) rotate(-9deg); }
    75% { transform: scale(.94) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
  }
  .cv-flame.pop { animation: cvFlamePop .5s var(--ease); }
  @media (prefers-reduced-motion: reduce) {
    .cv-page .cv-lock.is-locked, .cv-flame.pop { animation: none !important; }
  }

  @media (max-width: 600px) {
    .cv-wrap { padding: 20px 12px 70px; }
    .cv-name { font-size: 24px; }
    .cv-ava { width: 48px; height: 48px; }
    .cv-ask { font-size: 16px; }
    .cv-card { border-radius: 14px; padding: 8px 2px 2px; margin-left: -4px; margin-right: -4px; }
    .cv-save-form { grid-template-columns: 1fr; }
  }
`;

export default function CurveToday() {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, line: null, myDraw: null, band: null, drawCount: 0 });
  const [drawn, setDrawn] = useState(null); // weekly values pre-lock
  const [locking, setLocking] = useState(false);
  const [justLocked, setJustLocked] = useState(false); // button settle beat before the meter reveals
  const [tape, setTape] = useState(null);
  const [focus, setFocus] = useState(null); // {id, tick} — rail pick for the history viewer
  const { setRail } = useShellUI();

  const [chartKey, setChartKey] = useState(0); // bump to clear the chart on redraw
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  const claimedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);

  const load = useCallback(async () => {
    try {
      const [data, t] = await Promise.all([fetchToday(), fetchTape().catch(() => null)]);
      setState({ loading: false, ...data });
      setTape(t);
    } catch (e) {
      // A background refresh (resume/poll) failing shouldn't blank out an
      // already-loaded line — only a true first load falls back to empty.
      setState((s) => (s.line ? { ...s, loading: false } : { ...s, loading: false, line: null }));
    }
  }, []);

  // Signed in with leftover guest draws? Claim them into the account once.
  useEffect(() => {
    if (!user || claimedRef.current) return;
    const guest = getGuestToken();
    if (!guest) return;
    claimedRef.current = true;
    claimGuestDraws(guest)
      .then(() => clearGuestToken())
      .catch(() => {})
      .finally(load);
  }, [user, load]);

  useEffect(() => { load(); }, [load]);

  // Phones background the tab/PWA and fetches can hang or the data goes
  // stale; refresh the moment it's visible again (same fix as arenaSocket's
  // reconnect) plus a slow poll while it stays open, so a stuck load or a
  // leaguemate's new draw self-heals without a force-quit or a route change.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    const poll = setInterval(onVisible, 45000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(poll);
    };
  }, [load]);

  const lineId = state.line ? state.line.id : null;
  const wrapRef = useRef(null);

  // Rail row click: today's line scrolls back to the stage; anything else
  // swaps the history viewer to that chart (tick re-fires repeat picks).
  const handlePick = useCallback((draw) => {
    if (draw.line.id === lineId) {
      if (wrapRef.current) wrapRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setFocus((f) => ({ id: draw.id, tick: (f ? f.tick : 0) + 1 }));
    }
  }, [lineId]);

  useEffect(() => {
    setRail(<CurveRail user={user} tape={tape} todayLineId={lineId} onPick={handlePick} />);
    return () => setRail(null);
  }, [setRail, user, tape, lineId, handlePick]);

  // Streak pulse — counts up + the flame pops when it climbs.
  const streakCount = useCountUp((tape && tape.streak) || 0, { duration: 600 });

  // Next-morning ceremony — the most recently settled draw, shown once.
  const ceremony = useCurveCeremony(tape);
  const ceremonyShown = useReveal(!!ceremony, 20);

  // Consensus meter reveal — cascades in a beat after the lock actually commits.
  const meterShown = useReveal(!!state.myDraw, 20);

  const handleLock = async () => {
    if (!drawn || locking) return;
    setLocking(true);
    try {
      const result = await lockDraw(drawn);
      if (navigator.vibrate) navigator.vibrate(20);
      // The button settles into "Locked" first; the consensus meter commits
      // (and stagger-reveals) a beat later instead of painting instantly.
      setDrawn(null);
      setJustLocked(true);
      fetchTape().then(setTape).catch(() => {});
      setTimeout(() => {
        setState((s) => ({ ...s, myDraw: result.myDraw, band: result.band, drawCount: result.drawCount, othersEnd: result.othersEnd, roomLow: result.roomLow, roomHigh: result.roomHigh, strands: result.strands, endPercentile: result.endPercentile }));
        setJustLocked(false);
      }, 280);
    } catch (e) {
      if (e.status === 409) load(); // already locked elsewhere — resync
    } finally {
      setLocking(false);
    }
  };

  const { loading, line, myDraw, band, drawCount } = state;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="cv-page"><div className="cv-closed"><p>Warming up the griddle…</p></div></div>
      </>
    );
  }

  if (!line) {
    return (
      <>
        <style>{styles}</style>
        <div className="cv-page">
          <div className="cv-closed">
            <h1>No curve today.</h1>
            <p>A new one drops at midnight ET.</p>
          </div>
        </div>
      </>
    );
  }

  const locked = !!myDraw;
  const weekN = line.horizonWeeks;
  const settleLabel = resolveLabel(line.resolveDate); // "Week 6" or "Aug 28"
  const myEnd = locked ? myDraw.points[weekN - 1] : null;
  const crowdEnd = state.othersEnd != null ? state.othersEnd : null;
  const othersCount = Math.max(drawCount - 1, 0);
  const fmtV = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v);

  return (
    <>
      <style>{styles}</style>
      <div className="cv-page">
        <div className="cv-wrap" ref={wrapRef}>
          {ceremony ? (
            <div className={`cv-ceremony ft-ceremony${ceremonyShown ? ' show' : ''}`}>
              <span className="k">{ceremony.kicker}</span>
              <p>{ceremony.sentence}</p>
            </div>
          ) : null}

          <div className="cv-kick">
            <span>THE CURVE · {fmtDate(line.lineDate)}</span>
            <span className="n">
              {locked
                ? (tape && tape.streak ? (
                    <>
                      <span className={`cv-flame${streakCount.flashing && streakCount.direction === 'up' ? ' pop' : ''}`}>🔥</span>{' '}
                      <span className={streakCount.flashing ? `ft-tick ${streakCount.direction}` : 'ft-tick'}>{streakCount.display}</span> DAY STREAK
                    </>
                  ) : '')
                : drawCount > 0 ? `${drawCount} PLAYED TODAY` : 'BE FIRST TODAY'}
            </span>
          </div>

          <div className="cv-player">
            <div className="cv-ava">
              {line.player.sleeperId ? (
                <img src={`${SLEEPERCDN}${line.player.sleeperId}.jpg`} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : null}
              {(line.player.name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="cv-name">{line.player.name}</div>
              <div className="cv-meta">
                {[line.player.position, line.player.team].filter(Boolean).join(' · ')} · KTC VALUE TODAY {fmtV(line.startValue)}
                {line.player.ktcUrl ? (
                  <> · <a href={line.player.ktcUrl} target="_blank" rel="noopener noreferrer">View on KeepTradeCut →</a></>
                ) : null}
              </div>
            </div>
          </div>

          <p className="cv-ask">
            {locked || justLocked
              ? <>Locked in. <b>KTC updates every morning — come back to see how your line's doing.</b></>
              : <>Predict his <b>KTC value</b> by <b>{settleLabel}</b>. Draw what you believe.</>}
          </p>

          <div className="cv-card">
            <CurveChart
              key={chartKey}
              history={line.history}
              startValue={line.startValue}
              horizonWeeks={line.horizonWeeks}
              band={locked && (!state.strands || state.strands.length > 12) ? band : null}
              strands={locked && state.strands && state.strands.length <= 12 ? state.strands : null}
              myPoints={locked ? myDraw.points : null}
              interactive={!locked}
              onDrawChange={setDrawn}
              tall={isMobile}
              endLabel={fmtDate(line.resolveDate)}
              endWeekLabel={settleLabel.startsWith('Week') ? settleLabel.toUpperCase() : ''}
              ticks={(() => {
                const t = [];
                const hist = line.history || [];
                if (hist.length > 2) {
                  t.push({ kind: 'history', index: 0, label: fmtTick(hist[0].date) });
                  const mid = Math.floor(hist.length / 2);
                  t.push({ kind: 'history', index: mid, label: fmtTick(hist[mid].date) });
                }
                if (line.horizonWeeks >= 4 && line.startDate) {
                  const midWeek = Math.floor(line.horizonWeeks / 2);
                  const midDate = addDaysStr(line.startDate, midWeek * 7);
                  const wk = resolveLabel(midDate);
                  t.push({ kind: 'week', week: midWeek, label: wk.startsWith('Week') ? wk.toUpperCase() : fmtTick(midDate) });
                }
                return t;
              })()}
            />
            <div className="cv-legend">
              <span className="cv-lg h"><i></i> HIS KTC VALUE, LAST 3 MONTHS</span>
              <span className="cv-lg y"><i></i> YOUR LINE</span>
              {locked ? <span className="cv-lg b"><i></i> EVERYONE ELSE</span> : null}
              <span className="cv-lg r"><i></i> WHAT REALLY HAPPENS</span>
            </div>
          </div>

          {!locked ? (
            <>
              <div className="cv-actions">
                <button
                  className={`cv-lock${justLocked ? ' is-locked' : ''}`}
                  disabled={!drawn || locking || justLocked}
                  onClick={handleLock}
                >
                  {justLocked ? 'Locked' : locking ? 'Locking…' : drawn ? 'Lock it in' : 'Draw his line to unlock'}
                </button>
                {drawn && !justLocked ? (
                  <button className="cv-redraw" onClick={() => { setDrawn(null); setChartKey((k) => k + 1); }}>
                    ↺ Scrap it, draw again
                  </button>
                ) : null}
              </div>
              <div className="cv-note">One line a day. Locking is final — then you see everyone else's.</div>
            </>
          ) : (
            <>
              {crowdEnd != null && myEnd != null && othersCount > 0 && state.roomLow != null && state.roomHigh != null ? (
                (() => {
                  const span = Math.max(state.roomHigh - state.roomLow, 1);
                  const x = (v) => 6 + ((v - state.roomLow) / span) * 88;
                  const clampLabel = (p) => Math.max(11, Math.min(89, p));
                  const pos = x(myEnd);
                  const midPos = x(crowdEnd);
                  const strandEnds = (Array.isArray(state.strands) ? state.strands : [])
                    .map((s) => (Array.isArray(s) && s.length ? s[s.length - 1] : null))
                    .filter((v) => v != null);
                  const bandEnds =
                    !strandEnds.length && band && band.p25 && band.p75
                      ? [band.p25[band.p25.length - 1], band.p75[band.p75.length - 1]]
                      : null;
                  const JITTER = [22, 28, 24, 30];
                  return (
                    <div className={`cv-meter ft-stagger${meterShown ? ' ft-play' : ''}`}>
                      <div className="cv-meter-k"><span>LOW {fmtV(state.roomLow)}</span><span>TODAY'S CALLS</span><span>HIGH {fmtV(state.roomHigh)}</span></div>
                      <div className="cv-field">
                        <span className="fbase"></span>
                        {bandEnds ? (
                          <span
                            className="fband"
                            style={{
                              left: `${Math.min(x(bandEnds[0]), x(bandEnds[1]))}%`,
                              width: `${Math.max(Math.abs(x(bandEnds[1]) - x(bandEnds[0])), 2)}%`,
                            }}
                          ></span>
                        ) : null}
                        <span className="fmed" style={{ left: `${midPos}%` }}></span>
                        <span className="fmedlab" style={{ left: `${clampLabel(midPos)}%` }}>MEDIAN <b>{fmtV(crowdEnd)}</b></span>
                        {strandEnds.map((v, i) => (
                          <span key={i} className="fcall" style={{ left: `${x(v)}%`, bottom: JITTER[i % JITTER.length] }}></span>
                        ))}
                        <span className="fyou" style={{ left: `${pos}%` }}></span>
                        <span className="fyoulab" style={{ left: `${clampLabel(pos)}%` }}>YOU {fmtV(myEnd)}</span>
                      </div>
                      <div className="cv-meter-cap">
                        <span></span>
                        <span>{drawCount} {drawCount === 1 ? 'CALL' : 'CALLS'} TODAY</span>
                      </div>
                      <div className="cv-meter-verdict">
                        {myEnd > crowdEnd * 1.02 ? "You're above consensus on him."
                          : myEnd < crowdEnd * 0.98 ? "You're below consensus on him."
                          : "You're right at consensus."}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="cv-read">You're the first line in today — the gray strands appear as others draw theirs.</div>
              )}
              {!user ? (
                isMobile ? (
                  <SaveStreak streak={tape ? tape.streak : 0} />
                ) : (
                  <div className="cv-note">Playing as a guest — save your streak in the sidebar.</div>
                )
              ) : (
                <div className="cv-note">Locked in. Come back after the morning snapshot.</div>
              )}
              <AddToHomeScreen />

              <CurveHistorical />
            </>
          )}

          <CurveHistory tape={tape} isMobile={isMobile} focus={focus} todayLineId={line.id} />

          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}>
            <AdPlaceholder type="small_horizontal" />
          </div>
        </div>
      </div>
    </>
  );
}
